import { Idle, EventTargetInterruptSource } from '@ng-idle/core';
import { Router } from '@angular/router';
import { ElementRef, Injectable, NgZone } from '@angular/core';
import { AppUrls } from '../utility/appurls';
import { Moment } from 'moment-timezone';
import * as moment from 'moment-timezone';
import { IdentityService } from '../auth/identity.service';
import { EnvironmentService } from '../environment/environment.service';

@Injectable()
export class IdleService {
  readonly IDLE_TIME_MINS: number;
  readonly IDLE_COUNTDOWN_MINS: number;
  showIdleLogoutWarning: boolean;

  private readonly idleExpiryKey = 'ng2Idle.main.expiry';
  get idleExpiration(): Moment {
    const idleExpirationEpoch = +localStorage.getItem(this.idleExpiryKey);
    if (idleExpirationEpoch) {
      return moment(idleExpirationEpoch);
    } else {
      return null;
    }
  }

  constructor(
    private idle: Idle,
    private router: Router,
    private identityService: IdentityService,
    private environmentService: EnvironmentService,
    private zone: NgZone,
  ) {
    this.IDLE_TIME_MINS = environmentService.getEnvironment().idleTimeMins;
    this.IDLE_COUNTDOWN_MINS = environmentService.getEnvironment().idleCountdownMins;
  }

  initIdle(element: ElementRef) {
    // 60 * 28 = 1680 Seconds  [ 28 minutes idle ]
    this.idle.setIdle(this.IDLE_TIME_MINS * 60);

    // 60 * 2 = 120 Seconds [ 2 minutes idle ]
    // Sets the "Your session will expire in X minutes" modal countdown.
    this.idle.setTimeout(this.IDLE_COUNTDOWN_MINS * 60);

    // sets the interrupts like Keydown, scroll, mouse wheel, mouse down, and etc
    this.idle.setInterrupts([
      new EventTargetInterruptSource(
        element.nativeElement,
        'mousedown mouseup keydown keypress keyup',
      ),
    ]);

    this.idle.onIdleEnd.subscribe(() => {
      this.zone.run(() => {
        this.startIdle();
      });
    });

    this.idle.onTimeout.subscribe(() => {
      this.zone.run(() => {
        this.onIdleTimeout(true);
      });
    });

    this.idle.onInterrupt.subscribe(() => {
      if (!this.userHasTimedOut()) {
        this.zone.run(() => {
          this.identityService.refreshTokenAndIdentityIfExpiresSoon(30);
        });
      }
    });
  }

  startIdle() {
    this.idle.watch();
  }

  stopIdle() {
    this.idle.stop();
  }

  userHasTimedOut = () => moment().isAfter(this.idleExpiration);

  secondsUntilTimeOut = () => this.idleExpiration?.diff(moment(), 'seconds');

  onIdleRefresh = refreshUserInfo => {
    if (refreshUserInfo) {
      this.startIdle();
      this.identityService.refreshTokenAndIdentity(true);
    }
  };

  onIdleTimeout(showLogoutWarning: boolean = true) {
    if (showLogoutWarning) {
      this.showIdleLogoutWarning = true;
    }
    this.stopIdle();
    this.router.navigate([AppUrls.logout()], { queryParams: { doLogout: true, idle: true } });
  }
}
